import {
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  PaperProps,
  Stack,
  useTheme
} from '@mui/material';
import { listItemButtonClasses } from '@mui/material/ListItemButton';
import { transparentize } from 'polished';
import React, { Fragment } from 'react';
import { useMatch, useNavigate } from 'react-router-dom';
import { XClose } from 'untitledui-js-base';
import { Role } from '../types';
import { APP_HEADER_HEIGHT } from './AppHeader';
import { FlexBlock } from './FlexBlock';

export const SIZE = 250;

interface AppMenuItemButtonProps {
  icon: React.ReactNode;
  to: string;
  label?: string;
  badge?: React.ReactNode;
  onClick?: () => void;
}

const AppMenuItemButton = ({ to, icon, label, badge, onClick }: AppMenuItemButtonProps) => {
  const navigate = useNavigate();
  const isActive = Boolean(useMatch(`${to}/*`));
  const { palette } = useTheme();
  const activeBgColor = transparentize(0.94, palette.background.header);

  const handleMenuClick = () => {
    navigate(to);
    onClick?.();
  };

  return (
    <ListItem disablePadding>
      <ListItemButton
        disableRipple
        onClick={handleMenuClick}
        className={isActive ? 'active' : undefined}
        sx={{
          position: 'relative',
          borderRadius: 2,
          paddingY: 1,
          marginBottom: '2px',
          backgroundColor: isActive ? activeBgColor : 'transparent',
          '@media (prefers-reduced-motion: no-preference)': { transition: 'background-color 0.2s' },
          [`&.${listItemButtonClasses.focusVisible}, &:hover`]: {
            backgroundColor: isActive ? activeBgColor : 'grey.100'
          },
          [`&.${listItemButtonClasses.focusVisible}`]: { outline: '2px solid', outlineColor: 'primary.main' }
        }}
      >
        <Stack direction="row" alignItems="center">
          <ListItemIcon
            sx={{
              justifyContent: 'center',
              alignItems: 'center',
              color: isActive ? 'background.header' : 'inherit',
              height: 24,
              minWidth: 34
            }}
          >
            {icon}
          </ListItemIcon>
          <ListItemText
            primary={label}
            sx={{
              ml: 0,
              overflow: 'hidden',
              textWrap: 'nowrap',
              color: isActive ? 'background.header' : 'inherit'
            }}
            primaryTypographyProps={{ fontWeight: isActive ? 600 : 500 }}
          />
        </Stack>
        <FlexBlock />
        {badge}
      </ListItemButton>
    </ListItem>
  );
};

export type AppMenuItem = [string, string, React.ReactNode | undefined, React.ReactNode | undefined];

export interface AppMenuSection {
  title: string;
  items: AppMenuItem[];
  role?: Role;
}

export interface AppMenuProps extends PaperProps {
  sections: AppMenuSection[];
  menuOpen: boolean;
  onClose?: () => void;
}

export const AppMenu = ({ sx = {}, sections, menuOpen = false, onClose, ...props }: AppMenuProps) => {
  const drawerContent = (
    <Box display="flex" flexDirection="column" gap={2} paddingX={1}>
      <Box mb={1} ml={1} mt={2} display={{ md: 'none' }}>
        <IconButton onClick={onClose}>
          <XClose />
        </IconButton>
      </Box>
      {sections.map(({ title, items }, idx) => (
        <Fragment key={idx}>
          <Stack spacing={1} key={title}>
            <Box
              color="text.secondary"
              paddingLeft={2}
              typography="body3"
              sx={{ textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.06em', fontSize: '0.7rem' }}
            >
              {title}
            </Box>
            <List sx={{ p: 0 }}>
              {items.map(([name, href, icon, badge]) => (
                <AppMenuItemButton key={name} icon={icon} label={name} to={href} badge={badge} onClick={onClose} />
              ))}
            </List>
          </Stack>
          {idx !== sections.length - 1 && <Divider />}
        </Fragment>
      ))}
    </Box>
  );

  return (
    <Paper
      {...props}
      component="nav"
      sx={{
        minWidth: {
          md: SIZE
        },
        flexShrink: { sm: 0 },
        ...sx
      }}
    >
      {/* Mobile menu */}
      <Drawer
        variant="temporary"
        open={menuOpen}
        onClose={onClose}
        ModalProps={{
          keepMounted: true
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: SIZE
          }
        }}
      >
        {drawerContent}
      </Drawer>
      {/* Desktop menu */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', md: 'block' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: SIZE,
            top: `${APP_HEADER_HEIGHT + 20}px`,
            boxShadow: 'none',
            borderRight: '1px solid',
            borderColor: 'divider'
          }
        }}
        open
      >
        {drawerContent}
      </Drawer>
    </Paper>
  );
};
